import { Link } from "react-router-dom";
import { useT } from "../i18n/I18nProvider";
import { ChevronLeft } from "lucide-react";
import {
  ContentSection,
  AutoTOC,
  KeyPointsBlock,
  Sources,
} from "../components/contentTemplate";

// Section order mirrors the 6 H2 headings of the article body.
const SECTIONS = [
  { id: "affaire-szpital-poludniowy", title: "L'affaire du Szpital Południowy en quelques faits" },
  { id: "salon-vip-file-attente",     title: "Un salon VIP et une file d'attente à deux vitesses" },
  { id: "probleme-systemique",        title: "Un problème qui dépasse un seul hôpital" },
  { id: "reponse-gouvernement",       title: "La réponse du gouvernement" },
  { id: "public-prive",               title: "Public ou privé : ce que le système polonais offre vraiment" },
  { id: "expatrie",                   title: "Ce que le scandale change pour qui s'installe en Pologne" },
];

const SOURCES = [
  {
    url: "https://zero.pl/news/afera-w-szpitalu-poludniowym-kalendarium-wydarzen",
    label: "Zero.pl (Patryk Słowik / Kanał Zero)",
    description:
      "Enquêtes à l'origine de l'affaire : rémunérations de Dawid Kacprzyk, salon VIP et filière d'accueil accélérée, chronologie.",
  },
  {
    url: "https://www.rp.pl/ochrona-zdrowia/art44685961-waloryzacja-w-cieniu-afery-o-salonik-vip-i-milionowe-kontrakty-1-lipca-wchodzi-podwyzka-pensji-lekarzy-pielegniarek-i-salowych",
    label: "Rzeczpospolita (rp.pl)",
    description:
      "Montant de 1,6 million de zlotys, revalorisation du 1er juillet, analyses sur la dimension systémique et la perception de l'opinion.",
  },
  {
    url: "https://wiadomosci.wp.pl/ministra-zdrowia-o-aferze-wszczelismy-postepowania-wlasciwie-i-szybko-uwag-od-premiera-nie-bylo-7302137996810432a",
    label: "Wirtualna Polska",
    description:
      "Entretien avec la ministre Jolanta Sobierańska-Grenda : contrôle du 24 juin, 450 contrôles du NFZ, absence de conclusions définitives, mise en doute du lanceur d'alerte par le Premier ministre.",
  },
  {
    url: "https://forsal.pl/kraj/aktualnosci/artykuly/11272875,zmiany-w-systemie-ochrony-zdrowia-szefowa-resortu-i-prezes-nfz-zachowaja-stanowiska.html",
    label: "Forsal.pl",
    description:
      "Trois chantiers annoncés par Donald Tusk et maintien à leur poste de la ministre et du président du NFZ.",
  },
  {
    url: "https://dorzeczy.pl/kraj/902080/szybka-sciezka-i-salon-dla-ludzi-z-ko-i-ich-rodzin-na-sor.html",
    label: "Do Rzeczy (dorzeczy.pl)",
    description:
      "Filière rapide et salon VIP, statut municipal de l'hôpital et gouvernance, affaire de Mogilno, déclarations du ministre Kierwiński.",
  },
  {
    url: "https://niezalezna.pl/polska/patologia-w-sluzbie-zdrowia-system-trzeba-odpolitycznic-natychmiast/573200",
    label: "Niezalezna.pl",
    description:
      "Position de l'Ordre des médecins de Varsovie (dépolitisation, refus de la stigmatisation), sondage United Surveys by IBRiS.",
  },
  {
    url: "https://wiadomosci.wp.pl/niczego-nie-ukradlem-ujawnili-miliony-na-koncie-lekarza-7299843438430240a",
    label: "Gazeta Wyborcza, reprise par Wirtualna Polska",
    description:
      "Cas de Robert Górski à Zielona Góra (revenus 2021 issus de ses déclarations de patrimoine, point de vaccination, réponse de l'intéressé) et confirmation du retour des 500 000 zlotys dans l'affaire Kacprzyk.",
  },
  {
    url: "https://www.radiomaryja.pl/informacje/afera-w-szpitalu-poludniowym-w-warszawie-salonik-vip-dla-politykow-koalicji-obywatelskiej-badania-bez-kolejki-i-29-letni-milioner-radny-ko-w-bialym-kitlu/",
    label: "Radio Maryja / Do Rzeczy",
    description:
      "Projet de loi sur la collecte anonymisée des rémunérations (PESEL, licence d'exercice) et avertissement de l'autorité de protection des données (UODO).",
  },
];

// Shared classes for inline internal links in the body.
const INTERNAL_LINK = "text-[#0056D2] hover:underline";

// Footnote marker: <sup><a href="#source-n">[n]</a></sup>. Anchor jumps to
// the matching entry in the Sources list, which carries id="source-n".
function Note({ n }) {
  return (
    <sup data-testid={`note-${n}`} className="ml-0.5">
      <a
        href={`#source-${n}`}
        className="text-[#0056D2] hover:underline font-medium text-xs"
      >
        [{n}]
      </a>
    </sup>
  );
}

// Convert a plain-text paragraph containing [n] markers into a React fragment
// where each marker becomes a <Note n={...} />. Adjacent markers like [1][5]
// stay as distinct sup elements.
function nt(text) {
  const parts = text.split(/(\[\d+\])/g);
  return parts.map((part, idx) => {
    const m = /^\[(\d+)\]$/.exec(part);
    if (m) return <Note key={idx} n={Number(m[1])} />;
    return part;
  });
}

export default function BlogPostSanteSzpitalPoludniowy() {
  const { lp } = useT();

  return (
    <article
      className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-16"
      data-testid="blog-post-sante-szpital-poludniowy"
    >
      <Link
        to={lp("/blog")}
        className="inline-flex items-center gap-1.5 text-sm font-medium text-slate-500 hover:text-[#0056D2] mb-6"
        data-testid="blog-post-back"
      >
        <ChevronLeft className="w-4 h-4" />
        Retour au blog
      </Link>

      {/* Hero */}
      <header className="mb-10" data-testid="blog-post-hero">
        <span className="hej-chip" data-testid="blog-post-chip">
          <span className="w-1.5 h-1.5 rounded-full bg-[#DC143C]"></span>
          Mis à jour le 8 juillet 2026
        </span>
        <h1 className="font-display text-4xl sm:text-5xl font-bold text-slate-900 tracking-tight mt-4">
          Salaires de médecins et salon VIP : le scandale du Szpital Południowy
        </h1>
      </header>

      {/* Chapeau d'introduction */}
      <section
        className="space-y-4 text-slate-700 leading-relaxed text-lg mb-12"
        data-testid="blog-post-lede"
      >
        <p>
          Depuis la mi-juin 2026, un hôpital de Varsovie occupe le devant de la scène politique polonaise. Le Warszawski Szpital
          Południowy (hôpital du Sud de Varsovie) cumule trois reproches récurrents adressés à la santé publique du pays : des
          rémunérations de médecins sans commune mesure avec la norme, un passe-droit présumé pour des responsables politiques et
          une file d'attente qui varierait selon les relations. Pour qui envisage de s'installer en Pologne et compte s'appuyer sur
          le système public, l'affaire vaut surtout par ce qu'elle révèle du fonctionnement réel des soins, au-delà de son volet
          judiciaire encore ouvert.
        </p>
      </section>

      {/* Sommaire auto-généré */}
      <AutoTOC sections={SECTIONS} testid="blog-post-toc" />

      {/* À retenir */}
      <KeyPointsBlock testid="blog-post-key-points">
        <p className="font-display font-semibold text-slate-900 mb-3">
          Où en est l'affaire au 8 juillet 2026
        </p>
        <p>
          <strong>Établi.</strong> L'existence de l'affaire et son ampleur médiatique, l'ouverture de contrôles (ministère de la
          Santé le 24 juin, quelque 450 contrôles du NFZ dans divers établissements), le départ de Dawid Kacprzyk de la Coalition
          civique et de son mandat de conseiller, la résiliation de ses contrats avec l'hôpital, et une revalorisation des salaires
          planchers de la santé publique entrée en vigueur le 1er juillet (mécanisme annuel sans lien avec le scandale).
        </p>
        <p className="mt-3">
          <strong>Allégué ou en cours de contrôle.</strong> Le montant précis attribué à Kacprzyk, l'existence et le fonctionnement
          du salon VIP et de la filière d'accueil accélérée, les défaillances médicales évoquées par un ancien chef de service. La
          ministre indiquait fin juin ne pas connaître les noms des politiques concernés ni disposer des conclusions du NFZ,
          attendues autour de la mi-août.
        </p>
        <p className="mt-3">
          À ce stade, aucune autorité n'a rendu de conclusions définitives sur les points les plus sensibles.
        </p>
      </KeyPointsBlock>

      {/* Sections */}
      <ContentSection id="affaire-szpital-poludniowy" n={1} title="L'affaire du Szpital Południowy en quelques faits">
        <p>
          {nt(
            "Tout part d'un nom : Dawid Kacprzyk, moins de trente ans, médecin sans spécialisation, coordinateur du service des urgences (SOR) de cet établissement. Il était aussi militant de la Coalition civique (Koalicja Obywatelska, le parti du Premier ministre Donald Tusk) et ancien responsable de sa section jeunesse. Le journaliste Patryk Słowik, sur le portail Zero.pl, a révélé qu'il aurait perçu de l'ordre de 1,6 million de zlotys en un an, soit environ 375 000 euros[1]. Si ce montant se confirme, il représente à peu près quatorze années de salaire moyen polonais, celui-ci tournant autour de 9 500 zlotys bruts par mois[2]."
          )}
        </p>
        <p>
          {nt(
            "La chute a été rapide. Kacprzyk a quitté le parti puis son mandat de conseiller de l'arrondissement d'Ursus, et ses contrats avec l'hôpital ont été résiliés[1]. Après un audit, il a corrigé plusieurs factures et reversé plus de 500 000 zlotys à l'établissement, un geste que le maire de Varsovie Rafał Trzaskowski a lui-même interprété comme une forme de reconnaissance de responsabilité[1]. L'hôpital lui a ensuite retourné le virement, faute de base légale pour l'encaisser[7]. Le parquet régional de Varsovie a de son côté engagé des vérifications[3]."
          )}
        </p>
        <p>
          {nt(
            "Un élément pèse sur l'ensemble : le Szpital Południowy est un établissement municipal, détenu à 100 pour cent par la ville de Varsovie. Sa directrice, Anna Łukasik, fut vice-ministre de la Santé sous un gouvernement PO-PSL, et son conseil de surveillance comptait des élus liés à la majorité locale[5]."
          )}
        </p>
      </ContentSection>

      <ContentSection id="salon-vip-file-attente" n={2} title="Un salon VIP et une file d'attente à deux vitesses">
        <p>
          {nt(
            "Le second volet a fait basculer le sujet du fait divers au débat de société. Toujours d'après l'enquête de Patryk Słowik pour Zero.pl, une filière d'accueil accélérée aurait été réservée à des responsables de la Coalition civique et à leurs proches : admissions éclair, batteries d'examens dont l'ampleur n'a rien de standard aux urgences, et une pièce séparée où patienter, le « salonik VIP », loin des couloirs où attendaient les autres patients[1][5]. Interrogé, l'hôpital n'a pas démenti sur le fond et s'est surtout contenté de rappeler la loi sur la protection des données de santé[5]."
          )}
        </p>
        <p>
          {nt(
            "Des commentateurs y ont vu la résurgence de réflexes hérités de la République populaire, l'appartenance partisane tenant lieu de sésame[1]. Un ancien chef de la chirurgie de l'établissement, Emil Jędrzejewski, s'est présenté comme lanceur d'alerte et a évoqué en outre de graves défaillances d'organisation et de soins. Ces affirmations restent des allégations soumises aux enquêtes en cours, et Donald Tusk a publiquement mis en doute la crédibilité de leur auteur, qui s'expose lui-même à une action pour diffamation[3]. Le maire de Varsovie a affirmé n'avoir pas été informé au préalable et parlé d'événements qui n'auraient jamais dû se produire[5]."
          )}
        </p>
      </ContentSection>

      <ContentSection id="probleme-systemique" n={3} title="Un problème qui dépasse un seul hôpital">
        <p>
          {nt(
            "Plusieurs analystes, y compris dans une presse plutôt centriste, insistent sur un point : ce n'est pas l'histoire d'un homme, mais le symptôme d'un mal installé de longue date, toutes majorités confondues[2]. Deux exemples reviennent. À Zielona Góra, un médecin urgentiste également conseiller municipal, Robert Górski, a déclaré plus de 4,3 millions de zlotys de revenus en 2021 (plus d'un million d'euros), l'essentiel provenant d'un vaste point de vaccination contre la COVID-19 qui a administré plus de 56 000 doses cette année-là. L'information ressort de ses déclarations de patrimoine, révélées par Gazeta Wyborcza, et l'intéressé défend une activité légale, déclarée et contrôlée par le NFZ et le fisc[7]. À Mogilno, un hôpital dépourvu du service adéquat a confié des actes de neurochirurgie à une société privée selon une répartition très défavorable à l'établissement, une part majoritaire des fonds versés par le NFZ revenant aux médecins[5]."
          )}
        </p>
        <p>
          {nt(
            "Le nœud est toujours le même : le cumul d'une activité médicale et d'un mandat électif, des contrats NFZ très rémunérateurs, l'absence de contrôle réel du temps de travail et des conflits d'intérêts évidents. Les sondages traduisent une colère nette, une majorité de Polonais jugeant les salaires de médecins trop élevés[2]. L'Ordre des médecins de Varsovie met toutefois en garde contre la tentation de stigmatiser une prétendue « caste » et réclame surtout une dépolitisation urgente de la santé publique[6]."
          )}
        </p>
      </ContentSection>

      <ContentSection id="reponse-gouvernement" n={4} title="La réponse du gouvernement">
        <p>
          {nt(
            "Donald Tusk a annoncé qu'il ne chercherait pas de boucs émissaires parmi ses proches, tout en promettant que les responsables rendraient des comptes, certains devant la justice. Il a fixé trois chantiers : les anomalies du système de rémunération, la tarification des actes et l'organisation du travail hospitalier[4]. Un projet de loi a été adopté en urgence pour permettre à une agence publique de collecter des données anonymisées sur les rémunérations des soignants, à partir du numéro PESEL et du numéro de licence d'exercice, l'autorité de protection des données ayant aussitôt alerté sur un risque de profilage[8]."
          )}
        </p>
        <p>
          {nt(
            "La ministre de la Santé, Jolanta Sobierańska-Grenda, indique avoir lancé son propre contrôle dès le 24 juin, au lendemain des révélations les plus lourdes, et le NFZ a ouvert quelque 450 contrôles supplémentaires dont les premiers résultats sont attendus sous un mois et demi[3]. À ce jour, elle et le président du NFZ conservent leurs fonctions[4]. En pleine tourmente, une revalorisation des salaires planchers de la santé publique est entrée en vigueur le 1er juillet 2026, mécanisme annuel indexé sur le salaire moyen, sans lien avec l'affaire mais qui tombe au plus mauvais moment pour le gouvernement[2]."
          )}
        </p>
      </ContentSection>

      <ContentSection id="public-prive" n={5} title="Public ou privé : ce que le système polonais offre vraiment">
        <p>
          Au-delà des noms et des montants, l'épisode éclaire la réalité que rencontre un nouvel arrivant. Le système public
          polonais, financé par le Fonds national de santé (NFZ) sur un modèle bismarckien, offre une couverture large et un accès
          théoriquement égal pour tous. Dans les faits, il souffre de sous-effectifs, de délais parfois très longs pour les
          spécialistes et l'imagerie, et d'un financement sous tension. C'est ce qui pousse une part croissante de la population
          vers le privé pour les consultations et les diagnostics, une privatisation qui progresse sans le dire.
        </p>

        <div className="hej-card p-4 sm:p-6 my-6" data-testid="public-prive-table-wrapper">
          <div className="overflow-x-auto">
            <table className="w-full text-sm border-collapse" data-testid="public-prive-table">
              <thead>
                <tr className="border-b-2 border-slate-300">
                  <th className="text-left font-display font-semibold text-slate-900 px-3 py-3 align-top">Critère</th>
                  <th className="text-left font-display font-semibold text-slate-900 px-3 py-3 align-top">Secteur public (NFZ)</th>
                  <th className="text-left font-display font-semibold text-slate-900 px-3 py-3 align-top">Secteur privé</th>
                </tr>
              </thead>
              <tbody className="text-slate-700">
                <tr className="border-b border-slate-200">
                  <td className="px-3 py-3 align-top font-medium text-slate-900">Financement</td>
                  <td className="px-3 py-3 align-top">Cotisation santé obligatoire, gratuité au point de service pour l'essentiel</td>
                  <td className="px-3 py-3 align-top">Paiement direct, abonnement ou assurance (Medicover, LUX MED, entre autres)</td>
                </tr>
                <tr className="border-b border-slate-200">
                  <td className="px-3 py-3 align-top font-medium text-slate-900">Accès théorique</td>
                  <td className="px-3 py-3 align-top">Universel et égal pour tout assuré</td>
                  <td className="px-3 py-3 align-top">Ouvert à qui paie, sans condition d'affiliation</td>
                </tr>
                <tr className="border-b border-slate-200">
                  <td className="px-3 py-3 align-top font-medium text-slate-900">Délais spécialistes et imagerie</td>
                  <td className="px-3 py-3 align-top">Souvent longs, plusieurs mois pour certains actes</td>
                  <td className="px-3 py-3 align-top">Courts, rendez-vous rapides</td>
                </tr>
                <tr className="border-b border-slate-200">
                  <td className="px-3 py-3 align-top font-medium text-slate-900">Premier recours</td>
                  <td className="px-3 py-3 align-top">Médecin de famille déclaré</td>
                  <td className="px-3 py-3 align-top">Consultation libre</td>
                </tr>
                <tr>
                  <td className="px-3 py-3 align-top font-medium text-slate-900">Usage typique pour un expatrié</td>
                  <td className="px-3 py-3 align-top">Couverture de fond, hospitalisation, soins lourds</td>
                  <td className="px-3 py-3 align-top">Consultations courantes et bilans quand le délai public devient dissuasif</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </ContentSection>

      <ContentSection id="expatrie" n={6} title="Ce que le scandale change pour qui s'installe en Pologne">
        <p>
          L'enseignement pratique tient en peu de mots : la couverture publique existe et fonctionne, mais les délais conduisent
          souvent à compléter par le privé pour ce qui n'est pas urgent. Avant un départ, mieux vaut donc clarifier trois choses.
        </p>
        <p>
          Votre statut d'abord. Les ressortissants de l'Union européenne, de l'EEE et de la Suisse relèvent d'une procédure
          d'enregistrement simplifiée et ne sont pas soumis au régime des pays tiers. Nous détaillons ce parcours dans notre page
          sur les{" "}
          <Link to={lp("/demarches")} className={INTERNAL_LINK}>démarches administratives</Link>.
        </p>
        <p>
          Votre affiliation ensuite. Un emploi local ouvre en principe la couverture NFZ par la cotisation salariale, un point que
          nous traitons dans notre{" "}
          <Link to={lp("/travailler-pologne")} className={INTERNAL_LINK}>guide du travail en Pologne</Link>. Les retraités qui
          transfèrent leurs droits relèvent quant à eux de la coordination européenne et du formulaire S1, sujet abordé dans notre{" "}
          <Link to={lp("/retraite-pologne")} className={INTERNAL_LINK}>page sur la retraite</Link>.
        </p>
        <p>
          Le coût enfin. Une assurance ou un abonnement privé, souvent modéré au regard des tarifs d'Europe de l'Ouest, sert de
          complément pour raccourcir les délais. Le fonctionnement concret du NFZ, l'inscription et les options privées sont
          réunis dans notre{" "}
          <Link to={lp("/sante")} className={INTERNAL_LINK}>page dédiée aux soins</Link>.
        </p>
        <p>
          Le scandale du Szpital Południowy met en lumière la tension de fond du système : une égalité d'accès affichée, et des
          passe-droits qui la contredisent dès qu'existent les bonnes relations. Pour un futur résident, la leçon est moins
          politique que pratique. Elle invite à préparer sa couverture santé avant de s'installer, plutôt qu'à la découvrir dans
          une salle d'attente.
        </p>
      </ContentSection>

      {/* Sources */}
      <Sources sources={SOURCES} testid="blog-post-sources" />
    </article>
  );
}
